import { apiRequest } from "./api";
import type {
  AdminAuditLogListRequest,
  AdminAuditLogListResponse,
  AdminApproveEmailModerationRequest,
  AdminBlockContentRequest,
  AdminBusinessListingCountResponse,
  AdminBusinessListingDetailResponse,
  AdminBusinessListingListRequest,
  AdminBusinessListingListResponse,
  AdminBusinessVolunteerApprovalDecisionRequest,
  AdminBusinessVolunteerApprovalReviewResponse,
  AdminEmailModerationRequest,
  AdminEmailModerationReviewResponse,
  AdminFeedbackListRequest,
  AdminFeedbackListResponse,
  AdminGigCountResponse,
  AdminGigDetailResponse,
  AdminGigIssueDetailResponse,
  AdminGigIssueAdminNoteRequest,
  AdminGigIssueListRequest,
  AdminGigIssueListResponse,
  AdminGigListRequest,
  AdminGigListResponse,
  AdminNotificationDetailResponse,
  AdminNotificationListRequest,
  AdminNotificationListResponse,
  AdminPaymentActionRequest,
  AdminPaymentDetailResponse,
  AdminPaymentListRequest,
  AdminPaymentListResponse,
  AdminRequeueNotificationRequest,
  AdminResendVerificationEmailRequest,
  AdminResolveGigIssueRequest,
  AdminReviewListRequest,
  AdminReviewListResponse,
  AdminSendPasswordResetRequest,
  AdminTeenCompletedGigResponse,
  AdminUpdateGigIssueRequest,
  AdminUserCountResponse,
  AdminUserDetailResponse,
  AdminUserEmailListResponse,
  AdminUserListRequest,
  AdminUserListResponse,
} from "../types/adminTypes";

export const adminApi = {
  listUsers(payload: AdminUserListRequest): Promise<AdminUserListResponse> {
    return apiRequest<AdminUserListResponse>("/api/admin/users", {
      method: "POST",
      body: payload,
    });
  },

  countUsers(payload: AdminUserListRequest): Promise<AdminUserCountResponse> {
    return apiRequest<AdminUserCountResponse>("/api/admin/users/count", {
      method: "POST",
      body: payload,
    });
  },

  generateUserEmailList(payload: AdminUserListRequest): Promise<AdminUserEmailListResponse> {
    return apiRequest<AdminUserEmailListResponse>("/api/admin/users/email-list", {
      method: "POST",
      body: payload,
    });
  },

  getUserDetail(userId: string): Promise<AdminUserDetailResponse> {
    return apiRequest<AdminUserDetailResponse>(`/api/admin/users/${userId}`, {
      method: "GET",
    });
  },

  listTeenCompletedGigs(userId: string): Promise<AdminTeenCompletedGigResponse[]> {
    return apiRequest<AdminTeenCompletedGigResponse[]>(`/api/admin/users/${userId}/completed-gigs`, {
      method: "GET",
    });
  },

  resendVerificationEmail(userId: string, payload: AdminResendVerificationEmailRequest): Promise<void> {
    return apiRequest<void>(`/api/admin/users/${userId}/resend-verification-email`, {
      method: "POST",
      body: payload,
    });
  },

  sendPasswordReset(userId: string, payload: AdminSendPasswordResetRequest): Promise<void> {
    return apiRequest<void>(`/api/admin/users/${userId}/send-password-reset`, {
      method: "POST",
      body: payload,
    });
  },

  requestEmailBan(userId: string, payload: AdminEmailModerationRequest): Promise<void> {
    return apiRequest<void>(`/api/admin/users/${userId}/ban-email`, {
      method: "POST",
      body: payload,
    });
  },

  requestEmailUnban(userId: string, payload: AdminEmailModerationRequest): Promise<void> {
    return apiRequest<void>(`/api/admin/users/${userId}/unban-email`, {
      method: "POST",
      body: payload,
    });
  },

  getEmailModerationReview(requestId: string, token: string): Promise<AdminEmailModerationReviewResponse> {
    return apiRequest<AdminEmailModerationReviewResponse>(
      `/api/admin/email-moderation/${requestId}?token=${encodeURIComponent(token)}`,
      {
        method: "GET",
      },
    );
  },

  approveEmailModeration(
    requestId: string,
    payload: AdminApproveEmailModerationRequest,
  ): Promise<AdminEmailModerationReviewResponse> {
    return apiRequest<AdminEmailModerationReviewResponse>(`/api/admin/email-moderation/${requestId}/approve`, {
      method: "POST",
      body: payload,
    });
  },

  getBusinessVolunteerApprovalReview(requestId: string, token: string): Promise<AdminBusinessVolunteerApprovalReviewResponse> {
    return apiRequest<AdminBusinessVolunteerApprovalReviewResponse>(
      `/api/admin/business-volunteer-approvals/${requestId}?token=${encodeURIComponent(token)}`,
      {
        method: "GET",
      },
    );
  },

  approveBusinessVolunteerApproval(
    requestId: string,
    payload: AdminBusinessVolunteerApprovalDecisionRequest,
  ): Promise<AdminBusinessVolunteerApprovalReviewResponse> {
    return apiRequest<AdminBusinessVolunteerApprovalReviewResponse>(
      `/api/admin/business-volunteer-approvals/${requestId}/approve`,
      {
        method: "POST",
        body: payload,
      },
    );
  },

  rejectBusinessVolunteerApproval(
    requestId: string,
    payload: AdminBusinessVolunteerApprovalDecisionRequest,
  ): Promise<AdminBusinessVolunteerApprovalReviewResponse> {
    return apiRequest<AdminBusinessVolunteerApprovalReviewResponse>(
      `/api/admin/business-volunteer-approvals/${requestId}/reject`,
      {
        method: "POST",
        body: payload,
      },
    );
  },

  listGigs(payload: AdminGigListRequest): Promise<AdminGigListResponse> {
    return apiRequest<AdminGigListResponse>("/api/admin/gigs", {
      method: "POST",
      body: payload,
    });
  },

  countGigs(payload: AdminGigListRequest): Promise<AdminGigCountResponse> {
    return apiRequest<AdminGigCountResponse>("/api/admin/gigs/count", {
      method: "POST",
      body: payload,
    });
  },

  getGigDetail(gigId: string): Promise<AdminGigDetailResponse> {
    return apiRequest<AdminGigDetailResponse>(`/api/admin/gigs/${gigId}`, {
      method: "GET",
    });
  },

  blockGig(gigId: string, payload: AdminBlockContentRequest): Promise<AdminGigDetailResponse> {
    return apiRequest<AdminGigDetailResponse>(`/api/admin/gigs/${gigId}/block`, {
      method: "POST",
      body: payload,
    });
  },

  unblockGig(gigId: string, payload: AdminBlockContentRequest): Promise<AdminGigDetailResponse> {
    return apiRequest<AdminGigDetailResponse>(`/api/admin/gigs/${gigId}/unblock`, {
      method: "POST",
      body: payload,
    });
  },

  listBusinessListings(payload: AdminBusinessListingListRequest): Promise<AdminBusinessListingListResponse> {
    return apiRequest<AdminBusinessListingListResponse>("/api/admin/business-listings", {
      method: "POST",
      body: payload,
    });
  },

  countBusinessListings(payload: AdminBusinessListingListRequest): Promise<AdminBusinessListingCountResponse> {
    return apiRequest<AdminBusinessListingCountResponse>("/api/admin/business-listings/count", {
      method: "POST",
      body: payload,
    });
  },

  getBusinessListingDetail(listingId: string): Promise<AdminBusinessListingDetailResponse> {
    return apiRequest<AdminBusinessListingDetailResponse>(`/api/admin/business-listings/${listingId}`, {
      method: "GET",
    });
  },

  blockBusinessListing(listingId: string, payload: AdminBlockContentRequest): Promise<AdminBusinessListingDetailResponse> {
    return apiRequest<AdminBusinessListingDetailResponse>(`/api/admin/business-listings/${listingId}/block`, {
      method: "POST",
      body: payload,
    });
  },

  unblockBusinessListing(listingId: string, payload: AdminBlockContentRequest): Promise<AdminBusinessListingDetailResponse> {
    return apiRequest<AdminBusinessListingDetailResponse>(`/api/admin/business-listings/${listingId}/unblock`, {
      method: "POST",
      body: payload,
    });
  },

  listIssues(payload: AdminGigIssueListRequest): Promise<AdminGigIssueListResponse> {
    return apiRequest<AdminGigIssueListResponse>("/api/admin/issues", {
      method: "POST",
      body: payload,
    });
  },

  getIssueDetail(issueId: string): Promise<AdminGigIssueDetailResponse> {
    return apiRequest<AdminGigIssueDetailResponse>(`/api/admin/issues/${issueId}`, {
      method: "GET",
    });
  },

  updateIssueWorkflow(issueId: string, payload: AdminUpdateGigIssueRequest): Promise<AdminGigIssueDetailResponse> {
    return apiRequest<AdminGigIssueDetailResponse>(`/api/admin/issues/${issueId}/workflow`, {
      method: "POST",
      body: payload,
    });
  },

  addIssueAdminNote(issueId: string, payload: AdminGigIssueAdminNoteRequest): Promise<AdminGigIssueDetailResponse> {
    return apiRequest<AdminGigIssueDetailResponse>(`/api/admin/issues/${issueId}/notes`, {
      method: "POST",
      body: payload,
    });
  },

  resolveIssue(issueId: string, payload: AdminResolveGigIssueRequest): Promise<AdminGigIssueDetailResponse> {
    return apiRequest<AdminGigIssueDetailResponse>(`/api/admin/issues/${issueId}/resolve`, {
      method: "POST",
      body: payload,
    });
  },

  listPayments(payload: AdminPaymentListRequest): Promise<AdminPaymentListResponse> {
    return apiRequest<AdminPaymentListResponse>("/api/admin/payments", {
      method: "POST",
      body: payload,
    });
  },

  getPaymentDetail(paymentId: string, paymentType?: AdminPaymentDetailResponse["paymentType"]): Promise<AdminPaymentDetailResponse> {
    const query = paymentType ? `?paymentType=${encodeURIComponent(paymentType)}` : "";
    return apiRequest<AdminPaymentDetailResponse>(`/api/admin/payments/${paymentId}${query}`, {
      method: "GET",
    });
  },

  performPaymentAction(paymentId: string, payload: AdminPaymentActionRequest): Promise<AdminPaymentDetailResponse> {
    return apiRequest<AdminPaymentDetailResponse>(`/api/admin/payments/${paymentId}/actions`, {
      method: "POST",
      body: payload,
    });
  },

  listReviews(payload: AdminReviewListRequest): Promise<AdminReviewListResponse> {
    return apiRequest<AdminReviewListResponse>("/api/admin/reviews", {
      method: "POST",
      body: payload,
    });
  },

  listNotifications(payload: AdminNotificationListRequest): Promise<AdminNotificationListResponse> {
    return apiRequest<AdminNotificationListResponse>("/api/admin/notifications", {
      method: "POST",
      body: payload,
    });
  },

  listFeedback(payload: AdminFeedbackListRequest): Promise<AdminFeedbackListResponse> {
    return apiRequest<AdminFeedbackListResponse>("/api/admin/feedback", {
      method: "POST",
      body: payload,
    });
  },

  getNotificationDetail(notificationId: string): Promise<AdminNotificationDetailResponse> {
    return apiRequest<AdminNotificationDetailResponse>(`/api/admin/notifications/${notificationId}`, {
      method: "GET",
    });
  },

  requeueNotification(notificationId: string, payload: AdminRequeueNotificationRequest): Promise<void> {
    return apiRequest<void>(`/api/admin/notifications/${notificationId}/requeue`, {
      method: "POST",
      body: payload,
    });
  },

  listAudit(payload: AdminAuditLogListRequest): Promise<AdminAuditLogListResponse> {
    return apiRequest<AdminAuditLogListResponse>("/api/admin/audit", {
      method: "POST",
      body: payload,
    });
  },
};
